import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { statsAPI, entryAPI } from '../services/api';
import QuickLogButton from '../components/QuickLogButton';
import { handleApiError } from '../utils/errorHandler';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentEntries, setRecentEntries] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsResponse, entriesResponse] = await Promise.all([
        statsAPI.get(),
        entryAPI.getAll({ limit: 30 })
      ]);
      
      setStats(statsResponse.data);
      setRecentEntries(entriesResponse.data.slice(0, 5));
      
      // Prepare chart data
      const last30Days = entriesResponse.data.slice(0, 30);
      const chartLabels = last30Days.map(entry => entry.date).reverse();
      const painData = last30Days.map(entry => entry.pain_level || 0).reverse();
      const bloatingData = last30Days.map(entry => entry.bloating || 0).reverse();
      const fatigueData = last30Days.map(entry => entry.fatigue || 0).reverse();

      setChartData({
        labels: chartLabels,
        datasets: [
          {
            label: 'Pain Level',
            data: painData,
            borderColor: 'rgb(239, 68, 68)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            tension: 0.4,
            borderWidth: 3,
            pointBackgroundColor: 'rgb(239, 68, 68)',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            pointRadius: 6,
            pointHoverRadius: 8,
          },
          {
            label: 'Bloating',
            data: bloatingData,
            borderColor: 'rgb(20, 184, 166)',
            backgroundColor: 'rgba(20, 184, 166, 0.1)',
            tension: 0.4,
            borderWidth: 3,
            pointBackgroundColor: 'rgb(20, 184, 166)',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            pointRadius: 6,
            pointHoverRadius: 8,
          },
          {
            label: 'Fatigue',
            data: fatigueData,
            borderColor: 'rgb(99, 102, 241)',
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            tension: 0.4,
            borderWidth: 3,
            pointBackgroundColor: 'rgb(99, 102, 241)',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            pointRadius: 6,
            pointHoverRadius: 8,
          },
        ],
      });
      
      setLoading(false);
    } catch (error) {
      handleApiError(error, 'Error fetching dashboard data');
      setLoading(false);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#6b7280',
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 13,
            weight: '500',
          },
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#374151',
        bodyColor: '#6b7280',
        borderColor: 'rgba(255, 255, 255, 0.3)',
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
        font: {
          family: 'Inter, system-ui, sans-serif',
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(107, 114, 128, 0.1)',
          borderColor: 'rgba(107, 114, 128, 0.2)',
        },
        ticks: {
          color: '#9ca3af',
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        max: 10,
        grid: {
          color: 'rgba(107, 114, 128, 0.1)',
          borderColor: 'rgba(107, 114, 128, 0.2)',
        },
        ticks: {
          color: '#9ca3af',
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 11,
          },
          stepSize: 1,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    elements: {
      point: {
        hoverBorderWidth: 3,
      },
    },
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="glass-card p-8">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 border-4 border-primary-400/30 border-t-primary-500 rounded-full animate-spin"></div>
            <p className="text-neutral-600 font-medium">Loading your health data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 fade-in">
      {/* Hero Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gradient mb-3">Your Health Dashboard</h1>
        <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
          Welcome back! Monitor your symptoms, track your progress, and manage your IBD journey with personalized insights.
        </p>
      </div>

      {/* Quick Log Button - Premium placement */}
      <QuickLogButton />

      {/* Premium Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {/* Total Entries Card */}
        <div className="glass-card p-6 hover-lift group">
          <div className="flex items-center">
            <div className="p-3 bg-primary-gradient rounded-4xl shadow-premium group-hover:scale-110 transition-transform duration-300">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Total Entries</p>
              <p className="text-3xl font-bold text-neutral-800">{stats?.total_entries || 0}</p>
              <p className="text-xs text-neutral-500 mt-1">All time records</p>
            </div>
          </div>
        </div>

        {/* Average Pain Card */}
        <div className="glass-card p-6 hover-lift group">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-red-400 to-red-600 rounded-4xl shadow-premium group-hover:scale-110 transition-transform duration-300">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Avg Pain (30d)</p>
              <p className="text-3xl font-bold text-neutral-800">{stats?.avg_pain_last_30_days || 0}</p>
              <p className="text-xs text-neutral-500 mt-1">Out of 10 scale</p>
            </div>
          </div>
        </div>

        {/* Average Bloating Card */}
        <div className="glass-card p-6 hover-lift group">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-blue-400 to-blue-600 rounded-4xl shadow-premium group-hover:scale-110 transition-transform duration-300">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Avg Bloating (30d)</p>
              <p className="text-3xl font-bold text-neutral-800">{stats?.avg_bloating_last_30_days || 0}</p>
              <p className="text-xs text-neutral-500 mt-1">Out of 10 scale</p>
            </div>
          </div>
        </div>

        {/* Average Fatigue Card */}
        <div className="glass-card p-6 hover-lift group">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-green-400 to-green-600 rounded-4xl shadow-premium group-hover:scale-110 transition-transform duration-300">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Avg Fatigue (30d)</p>
              <p className="text-3xl font-bold text-neutral-800">{stats?.avg_fatigue_last_30_days || 0}</p>
              <p className="text-xs text-neutral-500 mt-1">Out of 10 scale</p>
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        {/* Premium Chart Card */}
        <div className="glass-card-strong p-8 hover-lift-subtle">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gradient">Symptom Trends</h2>
            <div className="p-2 bg-primary-gradient rounded-2xl">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
          {chartData ? (
            <div className="relative h-80">
              <Line data={chartData} options={chartOptions} />
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-white/10 to-transparent opacity-50 rounded-4xl"></div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="p-4 bg-primary-100/50 rounded-full mb-4">
                <svg className="w-12 h-12 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <p className="text-neutral-600 font-medium">No data available yet</p>
              <p className="text-sm text-neutral-500 mt-1">Start logging entries to see trends</p>
            </div>
          )}
        </div>

        {/* Premium Recent Entries Card */}
        <div className="glass-card-strong p-8 hover-lift-subtle">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gradient">Recent Entries</h2>
            <Link
              to="/entries"
              className="btn-secondary text-sm px-4 py-2 group"
            >
              <span>View All</span>
              <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
          
          {recentEntries.length > 0 ? (
            <div className="space-y-4">
              {recentEntries.map((entry, index) => (
                <div key={entry.id} className="glass-card p-4 hover-lift-subtle group border-l-4 border-primary-400">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-semibold text-neutral-800">
                          {entry.date}
                        </span>
                        <span className="text-xs text-neutral-500">at {entry.time}</span>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {entry.pain_level && (
                          <span className="inline-flex items-center px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                            Pain: {entry.pain_level}/10
                          </span>
                        )}
                        {entry.bloating && (
                          <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                            Bloating: {entry.bloating}/10
                          </span>
                        )}
                      </div>
                      {entry.meal_food && (
                        <p className="text-sm text-neutral-600 truncate">
                          🍽️ {entry.meal_food.length > 40 ? entry.meal_food.substring(0, 40) + '...' : entry.meal_food}
                        </p>
                      )}
                    </div>
                    <div className="ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-primary-600 font-bold text-xs">#{recentEntries.length - index}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="p-4 bg-teal-100/50 rounded-full mb-4">
                <svg className="w-12 h-12 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <p className="text-neutral-600 font-medium mb-2">No entries yet</p>
              <p className="text-sm text-neutral-500 mb-4">Start logging your symptoms and meals!</p>
              <button 
                onClick={() => document.querySelector('[data-testid="quick-log-button"]')?.click()}
                className="btn-primary text-sm px-4 py-2"
              >
                Create First Entry
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Premium Quick Actions */}
      <div className="mt-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gradient mb-2">Quick Actions</h2>
          <p className="text-neutral-600">Access key features with one click</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link
            to="/log"
            className="glass-card-strong p-6 text-center hover-lift group relative overflow-hidden"
          >
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary-400/20 to-primary-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            <div className="relative z-10">
              <div className="p-4 bg-primary-gradient rounded-4xl mx-auto w-16 h-16 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-premium">
                <span className="text-2xl">📝</span>
              </div>
              <h3 className="font-bold text-lg text-neutral-800 mb-2 group-hover:text-primary-700 transition-colors duration-300">Log New Entry</h3>
              <p className="text-sm text-neutral-600 group-hover:text-neutral-700 transition-colors duration-300">Record symptoms and meals</p>
            </div>
            
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 opacity-0 group-hover:opacity-100 group-hover:translate-x-full transition-all duration-700"></div>
          </Link>

          <Link
            to="/insights"
            className="glass-card-strong p-6 text-center hover-lift group relative overflow-hidden"
          >
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-teal-400/20 to-teal-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            <div className="relative z-10">
              <div className="p-4 bg-gradient-to-br from-teal-400 to-teal-600 rounded-4xl mx-auto w-16 h-16 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-premium">
                <span className="text-2xl">🧠</span>
              </div>
              <h3 className="font-bold text-lg text-neutral-800 mb-2 group-hover:text-teal-700 transition-colors duration-300">View Insights</h3>
              <p className="text-sm text-neutral-600 group-hover:text-neutral-700 transition-colors duration-300">AI-powered analysis</p>
            </div>
            
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 opacity-0 group-hover:opacity-100 group-hover:translate-x-full transition-all duration-700"></div>
          </Link>


          <Link
            to="/appointments"
            className="glass-card-strong p-6 text-center hover-lift group relative overflow-hidden"
          >
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-400/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            <div className="relative z-10">
              <div className="p-4 bg-gradient-to-br from-purple-400 to-purple-600 rounded-4xl mx-auto w-16 h-16 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-premium">
                <span className="text-2xl">👩‍⚕️</span>
              </div>
              <h3 className="font-bold text-lg text-neutral-800 mb-2 group-hover:text-purple-700 transition-colors duration-300">Appointments</h3>
              <p className="text-sm text-neutral-600 group-hover:text-neutral-700 transition-colors duration-300">Manage doctor visits</p>
            </div>
            
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 opacity-0 group-hover:opacity-100 group-hover:translate-x-full transition-all duration-700"></div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;